'use client'

import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Eye, EyeOff } from 'lucide-react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { apiPatch, apiPost, useCatalog, useTenants } from './entriesApi'
import type { CatalogItem } from './entryTypes'
import { trackEvent } from '@/lib/analytics'
import { SearchableMultiSelect } from '@shared/searchable-multi-select'
import { ManageEntityView, ManageEntityField, type ManageEntitySubmitResult } from './ManageEntityView'

type PlatformForm = { nameCode: string; name: string; description: string; logoUrl: string; url: string; tenantIds: string[]; isPublic: boolean }

// A new platform is hidden until someone decides it should reach the public.
const EMPTY_PLATFORM_FORM: PlatformForm = { nameCode: '', name: '', description: '', logoUrl: '', url: '', tenantIds: [], isPublic: false }

function formFromPlatform(p: CatalogItem): PlatformForm {
  return {
    nameCode: p.name_code,
    name: p.name,
    description: p.description ?? '',
    logoUrl: p.logo_url ?? '',
    url: p.url ?? '',
    tenantIds: p.tenant_ids ?? [],
    isPublic: p.is_public === true,
  }
}

/** Public / Hidden pill, used in the list and above the form. */
export function PlatformVisibilityPill({ isPublic, className = '' }: { isPublic: boolean | undefined; className?: string }) {
  return isPublic ? (
    <span className={`inline-flex items-center gap-1 rounded-full border border-success/40 bg-success-surface px-1.5 py-0.5 text-[10px] font-semibold text-success ${className}`} title="Shown on Burla ou Não / É Legítimo">
      <Eye className="h-3 w-3" /> Public
    </span>
  ) : (
    <span className={`inline-flex items-center gap-1 rounded-full border border-border bg-subtle/10 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground ${className}`} title="Hidden: only the trust center sees this platform and its entries">
      <EyeOff className="h-3 w-3" /> Hidden
    </span>
  )
}

function PlatformLogo({ url, className }: { url?: string | null; className: string }) {
  if (!url) return <div className={`${className} flex items-center justify-center rounded bg-subtle/10 text-[9px] uppercase opacity-50`}>—</div>
  return <img src={url} alt="" className={`${className} rounded object-contain`} />
}

export function ManagePlatformsView({ theme }: { theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const { catalog, loading: catalogLoading, refetch: refetchCatalog } = useCatalog()
  const { tenants, loading: tenantsLoading } = useTenants()

  const onSubmit = async (form: PlatformForm, mode: 'create' | 'edit', id: string | number | undefined): Promise<ManageEntitySubmitResult> => {
    const payload = {
      nameCode: form.nameCode,
      name: form.name,
      description: form.description || null,
      logoUrl: form.logoUrl || null,
      url: form.url,
      tenantIds: form.tenantIds,
      isPublic: form.isPublic,
    }

    const result = mode === 'create'
      ? await apiPost<{ id: number }>('/platforms', payload)
      : await apiPatch('/platforms', { ...payload, id })

    if (!result.success) {
      const errorMessage = typeof result.error === 'string' ? result.error : 'Failed to save'
      trackEvent({ event_type: 'action', event_name: mode === 'create' ? 'register_platform_submit' : 'update_platform_submit', component: 'manage_platform_form', field_1_key: 'step', field_1_value: 'error', field_2_key: 'message', field_2_value: errorMessage.slice(0, 200) })
      return { success: false, error: errorMessage }
    }

    trackEvent({ event_type: 'action', event_name: mode === 'create' ? 'register_platform_submit' : 'update_platform_submit', component: 'manage_platform_form', field_1_key: 'step', field_1_value: 'success' })
    return { success: true, id: mode === 'create' ? result.data?.id : id }
  }

  return (
    <ManageEntityView<CatalogItem, PlatformForm>
      theme={theme}
      title="Platforms"
      createLabel="Create Platform"
      items={catalog.platforms}
      loading={catalogLoading}
      getId={(p) => p.id}
      getName={(p) => p.name}
      getCode={(p) => p.name_code}
      getCreatedAt={(p) => p.created_at}
      getUpdatedAt={(p) => p.updated_at}
      renderListMeta={(p) => (
        <span className="inline-flex items-center gap-1.5">
          <PlatformLogo url={p.logo_url} className="h-4 w-4" />
          <PlatformVisibilityPill isPublic={p.is_public} />
        </span>
      )}
      emptyForm={EMPTY_PLATFORM_FORM}
      formFromItem={formFromPlatform}
      isFormValid={(form) => !!form.nameCode && !!form.name}
      invalidMessage="Fill in Name Code and Name"
      onSubmit={onSubmit}
      onAfterSuccess={refetchCatalog}
      renderFields={(form, setForm) => (
        <>
          <div className="mb-4 flex items-center gap-3">
            <PlatformLogo url={form.logoUrl} className="h-12 w-12 shrink-0" />
            <PlatformVisibilityPill isPublic={form.isPublic} />
          </div>

          <ManageEntityField label="Visibility" theme={theme}>
            <label className="flex cursor-pointer items-start gap-3">
              <Switch
                checked={form.isPublic}
                onCheckedChange={(checked) => setForm((p) => p && { ...p, isPublic: checked === true })}
                aria-label="Public"
                data-action="platform-public-toggle"
              />
              <span className="text-[12px] leading-snug">
                <span className={`font-semibold ${c.text}`}>{form.isPublic ? 'Public — shown on Burla ou Não / É Legítimo' : 'Hidden — trust center only'}</span>
                <span className={`block ${c.faint}`}>Hidden platforms and their entries never reach the consumer apps (feed, search, SEO pages). New platforms start hidden.</span>
              </span>
            </label>
          </ManageEntityField>

          <ManageEntityField label="Name Code *" theme={theme}>
            <Input className={c.input} value={form.nameCode} onChange={(e) => setForm((p) => p && { ...p, nameCode: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Name *" theme={theme}>
            <Input className={c.input} value={form.name} onChange={(e) => setForm((p) => p && { ...p, name: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Description" theme={theme}>
            <Textarea className={c.input} value={form.description} onChange={(e) => setForm((p) => p && { ...p, description: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Logo URL" theme={theme}>
            <Input className={c.input} value={form.logoUrl} onChange={(e) => setForm((p) => p && { ...p, logoUrl: e.target.value })} />
          </ManageEntityField>
          <ManageEntityField label="Platform Domain *" theme={theme}>
            <Input className={c.input} value={form.url} onChange={(e) => setForm((p) => p && { ...p, url: e.target.value })} placeholder="https://example.com" />
          </ManageEntityField>
          <ManageEntityField label="Tenants" theme={theme}>
            <SearchableMultiSelect
              theme={theme}
              loading={tenantsLoading}
              placeholder="Select tenants…"
              searchPlaceholder="Search by code or name…"
              options={tenants.map((t) => ({ id: t.id, code: t.name_code, label: t.name }))}
              selectedIds={form.tenantIds}
              onChange={(ids) => setForm((p) => p && { ...p, tenantIds: ids as string[] })}
            />
          </ManageEntityField>
        </>
      )}
    />
  )
}
